import { VaccinationDocumentUploader } from '@/components/VaccinationDocumentUploader';
import { ArrowLeft, FileText, PawPrint } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CustomerFeedingPlanPanel } from '../components/CustomerFeedingPlanPanel';
import { PetAssessmentCard } from '../components/PetAssessmentCard';
import type { PetSummary } from '../components/PetCard';
import { StatusPill } from '../components/StatusPill';
import { VetSharingPanel } from '../components/VetSharingPanel';
import { customerApi } from '../customer_api';
import { formatDate } from '../lib/format';

interface Vaccination {
  id: string;
  vaccineName: string;
  vaccineSlug: string;
  administeredOn: string | null;
  expiresOn: string | null;
  status: 'valid' | 'expiring' | 'expired';
  documentFileId?: string | null;
}

interface PetDetailResponse {
  pet: PetSummary;
  vaccinations: Vaccination[];
}

function LabelValue({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '1rem',
        padding: '0.3rem 0',
        fontSize: '0.9rem',
      }}
    >
      <span style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value ?? '—'}</span>
    </div>
  );
}

import type React from 'react';

export function PetDetail() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<PetDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  function refresh() {
    if (!id) return;
    customerApi<PetDetailResponse>(`/api/customer/pets/${id}`)
      .then(setData)
      .catch(() => setError('Could not load pet. Please try again.'));
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <Link
          to="/account/pets"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.85rem',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
            textDecoration: 'none',
            marginBottom: '1.5rem',
          }}
        >
          <ArrowLeft size={14} />
          Pets
        </Link>

        {error && <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{error}</p>}

        {!error && data === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {data !== null && (
          <>
            {/* Pet card */}
            <div
              style={{
                display: 'flex',
                gap: '1.25rem',
                alignItems: 'flex-start',
                marginBottom: '1.5rem',
              }}
            >
              <div
                style={{
                  width: '5rem',
                  height: '5rem',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: 'oklch(0.94 0.005 80)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {data.pet.photoUrl ? (
                  <img
                    src={data.pet.photoUrl}
                    alt={data.pet.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <PawPrint
                    size={28}
                    style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}
                  />
                )}
              </div>
              <div>
                <h1
                  style={{
                    fontSize: '1.75rem',
                    fontWeight: 500,
                    letterSpacing: '-0.02em',
                    margin: '0 0 0.25rem',
                  }}
                >
                  {data.pet.name}
                </h1>
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                  }}
                >
                  {data.pet.species}
                  {data.pet.breed ? ` · ${data.pet.breed}` : ''}
                </p>
              </div>
            </div>

            {/* Pet details */}
            <div
              style={{
                padding: '1.25rem',
                background: 'var(--surface, white)',
                borderRadius: '12px',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
              }}
            >
              <div
                style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                  marginBottom: '0.75rem',
                }}
              >
                Details
              </div>
              {data.pet.sizeCategory && <LabelValue label="Size" value={data.pet.sizeCategory} />}
              {data.pet.color && <LabelValue label="Colour" value={data.pet.color} />}
              {data.pet.microchip && (
                <LabelValue
                  label="Microchip"
                  value={<span className="num">{data.pet.microchip}</span>}
                />
              )}
            </div>

            {/* Evaluation day (migration 0099) */}
            {id && <PetAssessmentCard petId={id} />}

            {/* Vaccinations */}
            <div
              style={{
                marginTop: '1.5rem',
                padding: '1.25rem',
                background: 'var(--surface, white)',
                borderRadius: '12px',
                border: '1px solid var(--border, oklch(0.915 0.005 80))',
              }}
            >
              <div
                style={{
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                  marginBottom: '0.75rem',
                }}
              >
                Vaccinations
              </div>

              {data.vaccinations.length === 0 ? (
                <p
                  style={{
                    margin: 0,
                    fontSize: '0.9rem',
                    color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                  }}
                >
                  No vaccinations on record.
                </p>
              ) : (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '0.875rem',
                  }}
                >
                  <thead>
                    <tr>
                      {(['Vaccine', 'Administered', 'Expires', 'Status', 'Record'] as const).map(
                        (h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: 'left',
                              fontSize: '0.72rem',
                              fontWeight: 500,
                              color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                              paddingBottom: '0.5rem',
                              borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))',
                            }}
                          >
                            {h}
                          </th>
                        ),
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {data.vaccinations.map((v) => (
                      <tr key={v.id}>
                        <td style={{ padding: '0.5rem 0', paddingRight: '0.75rem' }}>
                          {v.vaccineName}
                        </td>
                        <td style={{ padding: '0.5rem 0', paddingRight: '0.75rem' }}>
                          <span className="num">{formatDate(v.administeredOn)}</span>
                        </td>
                        <td style={{ padding: '0.5rem 0', paddingRight: '0.75rem' }}>
                          <span className="num">{formatDate(v.expiresOn)}</span>
                        </td>
                        <td style={{ padding: '0.5rem 0', paddingRight: '0.75rem' }}>
                          <StatusPill status={v.status} />
                        </td>
                        <td style={{ padding: '0.5rem 0' }}>
                          {v.documentFileId ? (
                            <a
                              href={`/api/customer/pets/${data.pet.id}/vaccinations/${v.id}/document`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                fontSize: '0.8rem',
                                color: 'var(--primary, oklch(0.45 0.18 250))',
                                textDecoration: 'none',
                              }}
                            >
                              <FileText size={12} /> View
                            </a>
                          ) : (
                            <VaccinationDocumentUploader
                              scope="customer"
                              petId={data.pet.id}
                              vaccinationId={v.id}
                              label="Upload"
                              onUploaded={() => refresh()}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}

        {/* Structured feeding plan (migration 0101) — owner-authored. */}
        {data ? <CustomerFeedingPlanPanel petId={data.pet.id} /> : null}
        {data ? <VetSharingPanel petId={data.pet.id} /> : null}
      </div>
    </main>
  );
}
